import { z } from 'zod';
/**
 * Error codes from Docs/API.md §1.6. The list is the contract: the API must never
 * return a code that is not here, and clients may switch on these values.
 * Codes are added here first, then mapped to an HTTP status in apps/api.
 */
export const ERROR_CODES = [
    // 400
    'VALIDATION_FAILED',
    'BAD_REQUEST',
    'UNSUPPORTED_MEDIA_TYPE',
    // 401
    'UNAUTHENTICATED',
    'SESSION_EXPIRED',
    // 403
    'FORBIDDEN',
    'MFA_REQUIRED',
    'MFA_STEP_UP_REQUIRED',
    'IMPERSONATION_BLOCKED',
    'CSRF_ORIGIN_MISMATCH',
    'FEATURE_DISABLED',
    'TENANT_SUSPENDED',
    // 404
    'NOT_FOUND',
    'TENANT_NOT_FOUND',
    // 409
    'CONFLICT',
    'SLOT_TAKEN',
    /** M15: the tenant's scheduling rules decided `contact_office` for this draft. */
    'SCHEDULING_BLOCKED',
    'RESERVATION_CONFLICT',
    'IDEMPOTENCY_IN_PROGRESS',
    'DUPLICATE',
    'VERSION_CONFLICT',
    // 410
    'RESERVATION_EXPIRED',
    'DRAFT_EXPIRED',
    'LINK_EXPIRED',
    /**
     * The earlier slot the clinic was holding is no longer held (M10.3, FR-BOOK-16).
     *
     * Its own code rather than `RESERVATION_EXPIRED` because the portal says a different
     * sentence for each: a reservation that ran out is "your time ran out, here are some
     * others", and an offer that closed is "somebody else took it, you are still on the
     * list". `410` for both - the thing named by the request genuinely existed and is
     * gone.
     */
    'OFFER_EXPIRED',
    // 413
    'PAYLOAD_TOO_LARGE',
    // 422
    'POLICY_WINDOW_CLOSED',
    'RESCHEDULE_LIMIT',
    'INVALID_STATE_TRANSITION',
    'IDEMPOTENCY_MISMATCH',
    'ELIGIBILITY_DATA_ERROR',
    'INTAKE_INCOMPLETE',
    'PAYMENT_METHOD_REQUIRED',
    /**
     * A notification template body referenced a variable its kind does not permit, or a
     * send tried to substitute one (M9.1, API.md §2.9, FR-NOTIF-05).
     *
     * Its own code rather than `VALIDATION_FAILED` because it is the PHI control, not a
     * typo: the editor has to be able to say *which* variable and why, and an audit later
     * has to be able to count the times somebody tried.
     */
    'TEMPLATE_VARIABLE_NOT_ALLOWED',
    // 429
    'RATE_LIMITED',
    // 5xx
    'INTERNAL',
    'INTEGRATION_ERROR',
    'INTEGRATION_UNAVAILABLE',
    'NOT_READY',
    /** Maintenance mode is on: every clinic-facing surface is closed (M12.6). */
    'MAINTENANCE',
    'INTEGRATION_TIMEOUT',
];
export const ErrorCodeSchema = z.enum(ERROR_CODES);
/** One zod issue, re-shaped for the wire (Docs/API.md §1.6, paths in camelCase). */
export const ValidationIssueSchema = z.object({
    path: z.array(z.union([z.string(), z.number()])),
    code: z.string(),
    message: z.string(),
});
/**
 * The only envelope returned for a non-2xx response.
 * `message` is safe to display; `details` is schema-defined per code; stack traces
 * and vendor error text never appear (rules §14.8, §23.8).
 */
export const ErrorEnvelopeSchema = z.object({
    error: z.object({
        code: ErrorCodeSchema,
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
        correlationId: z.string(),
    }),
});
export const ValidationErrorDetailsSchema = z.object({
    issues: z.array(ValidationIssueSchema),
});
//# sourceMappingURL=errors.js.map